import {
  TEMPLATE_ID_DUAL_SHARE_CLASS,
  TEMPLATE_ID_LONG_TERM_VALUE_FUND,
  TEMPLATE_ID_OMIT_ETHIK_KONTROVERSE,
  templateHasPieAdditionChart,
} from './templateConstants';
import {
  getPieTitleIndex as getPieTitleIndexForTemplate,
  isStalePieTitleFromLegacyFieldOrder,
  templateUsesRemappedPieTitleIndices,
} from './pieChartDisplay';

export type PieChartHeadingKey =
  | 'gesamtportfolio'
  | 'branchen'
  | 'regionen'
  | 'marktkapitalisierung'
  | 'pie_addition';

const DEFAULT_PIE_CHART_HEADINGS: Record<PieChartHeadingKey, string> = {
  gesamtportfolio: 'TOP-5-AKTIEN (GESAMTPORTFOLIO)',
  branchen: 'BRANCHEN',
  regionen: 'REGIONEN',
  marktkapitalisierung: 'MARKTKAPITALISIERUNG',
  pie_addition: 'MARKTKAPITALISIERUNG',
};

/** branchen → VERMÖGENSVERTEILUNG, regionen → BRANCHEN (AKTIENANTEIL), marktkapitalisierung → REGIONEN (AKTIENANTEIL) */
const THREE_PIE_RENAMED_HEADINGS: Partial<Record<PieChartHeadingKey, string>> = {
  branchen: 'VERMÖGENSVERTEILUNG',
  regionen: 'BRANCHEN (AKTIENANTEIL)',
  marktkapitalisierung: 'REGIONEN (AKTIENANTEIL)',
};

/** SHV Long-Term Value Fund (6995ea9e4c4ced0db3932d1a) */
const LONG_TERM_VALUE_FUND_PIE_CHART_HEADINGS: Partial<Record<PieChartHeadingKey, string>> = {
  marktkapitalisierung: 'VERMÖGENSVERTEILUNG',
  branchen: 'BRANCHEN (AKTIENANTEIL)',
  regionen: 'REGIONEN (AKTIENANTEIL)',
  pie_addition: 'MARKTKAPITALISIERUNG (AKTIENANTEIL)',
};

const LONG_TERM_VALUE_FUND_NUMBERED_HEADINGS: Partial<Record<PieChartHeadingKey, string>> = {
  marktkapitalisierung: '1. VERMÖGENSVERTEILUNG',
  branchen: '2. BRANCHEN (AKTIENANTEIL)',
  regionen: '3. REGIONEN (AKTIENANTEIL)',
};

/** Dual share-class (6995ec804c4ced0db3932d1b) */
const DUAL_SHARE_CLASS_PIE_CHART_HEADINGS: Partial<Record<PieChartHeadingKey, string>> = {
  ...THREE_PIE_RENAMED_HEADINGS,
  pie_addition: 'MARKTKAPITALISIERUNG (AKTIENANTEIL)',
};

/** Value Focus Fund — omits ETHIK/KONTROVERSE (6995ed834c4ced0db3932d1c) */
const OMIT_ETHIK_KONTROVERSE_PIE_CHART_HEADINGS: Partial<Record<PieChartHeadingKey, string>> = {
  ...THREE_PIE_RENAMED_HEADINGS,
  pie_addition: 'MARKTKAPITALISIERUNG (AKTIENANTEIL)',
};

const TEMPLATE_PIE_HEADINGS: Record<string, Partial<Record<PieChartHeadingKey, string>>> = {
  [TEMPLATE_ID_LONG_TERM_VALUE_FUND]: LONG_TERM_VALUE_FUND_PIE_CHART_HEADINGS,
  [TEMPLATE_ID_DUAL_SHARE_CLASS]: DUAL_SHARE_CLASS_PIE_CHART_HEADINGS,
  [TEMPLATE_ID_OMIT_ETHIK_KONTROVERSE]: OMIT_ETHIK_KONTROVERSE_PIE_CHART_HEADINGS,
};

const TEMPLATE_PIE_LEGACY_NUMBERED: Record<string, Partial<Record<PieChartHeadingKey, string>>> = {
  [TEMPLATE_ID_LONG_TERM_VALUE_FUND]: LONG_TERM_VALUE_FUND_NUMBERED_HEADINGS,
};

function getTemplatePieHeadings(templateId?: string): Partial<Record<PieChartHeadingKey, string>> | undefined {
  if (!templateId) return undefined;
  return TEMPLATE_PIE_HEADINGS[templateId];
}

/** Templates that show a fourth donut (pie_addition) below the three standard pies */
export function templateHasFourthPieChart(templateId?: string): boolean {
  return templateHasPieAdditionChart(templateId);
}

export function getDefaultPieChartHeading(
  templateId: string | undefined,
  key: PieChartHeadingKey,
): string {
  const custom = getTemplatePieHeadings(templateId)?.[key];
  return custom ?? DEFAULT_PIE_CHART_HEADINGS[key];
}

/** Resolve display title: custom headings / pie_titles, then template-specific defaults */
export function resolvePieChartHeading(
  templateId: string | undefined,
  key: PieChartHeadingKey,
  options?: {
    headings?: Record<string, string>;
    pieTitles?: string[];
  },
): string {
  const custom = options?.headings?.[key]?.trim();
  const idx = getPieTitleIndexForTemplate(templateId, key);
  const fromPieTitles =
    idx !== undefined ? options?.pieTitles?.[idx]?.trim() : undefined;

  const templateHeadings = getTemplatePieHeadings(templateId);
  if (templateHeadings) {
    const legacyDefault = DEFAULT_PIE_CHART_HEADINGS[key];
    const numberedLegacy = TEMPLATE_PIE_LEGACY_NUMBERED[templateId!]?.[key];
    const templateDefault = templateHeadings[key];
    const isLegacyTitle = (value: string | undefined) =>
      !!value &&
      (value === legacyDefault ||
        value === numberedLegacy ||
        value === templateDefault);
    const usesRemappedIndices = templateUsesRemappedPieTitleIndices(templateId);
    const isStaleRemappedPieTitle = (value: string | undefined) =>
      usesRemappedIndices && isStalePieTitleFromLegacyFieldOrder(value);

    if (!custom && !fromPieTitles) {
      return getDefaultPieChartHeading(templateId, key);
    }
    if (
      isLegacyTitle(custom) ||
      isLegacyTitle(fromPieTitles) ||
      isStaleRemappedPieTitle(fromPieTitles)
    ) {
      return getDefaultPieChartHeading(templateId, key);
    }
    if (custom) return custom;
    if (fromPieTitles) return fromPieTitles;
    return getDefaultPieChartHeading(templateId, key);
  }

  if (custom) return custom;
  if (fromPieTitles) return fromPieTitles;
  return getDefaultPieChartHeading(templateId, key);
}
